import { lazy, Suspense, useMemo } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { Scene3D } from './components/scene/Scene3D';
import { RouteShapeAccent } from './components/ShapeAccent';
import { Cursor } from './components/Cursor';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { Analytics } from './components/Analytics';
import { StickyWhatsApp } from './components/StickyWhatsApp';
import { LocaleRoute, LegacyLocaleRedirect } from './components/LocaleSync';
import { HomePage } from './pages/HomePage';
import { useMediaQuery } from './hooks';
import { detectPerfTier, PERF_BUDGET } from './perf';
import { DEFAULT_LOCALE, stripLocale } from './lib/localePath';

const BlogPage = lazy(() => import('./pages/BlogPage').then((m) => ({ default: m.BlogPage })));
const BlogPostPage = lazy(() =>
  import('./pages/BlogPostPage').then((m) => ({ default: m.BlogPostPage }))
);
const ProjectsPage = lazy(() =>
  import('./pages/ProjectsPage').then((m) => ({ default: m.ProjectsPage }))
);
const ProjectPage = lazy(() =>
  import('./pages/ProjectPage').then((m) => ({ default: m.ProjectPage }))
);
const LegalPage = lazy(() => import('./pages/LegalPage').then((m) => ({ default: m.LegalPage })));
const ServicePage = lazy(() =>
  import('./pages/ServicePage').then((m) => ({ default: m.ServicePage }))
);
const CityPage = lazy(() => import('./pages/CityPage').then((m) => ({ default: m.CityPage })));
const QuotePage = lazy(() => import('./pages/QuotePage').then((m) => ({ default: m.QuotePage })));

function AppChrome() {
  const { pathname } = useLocation();
  const bare = stripLocale(pathname);
  const isHome = bare === '/';
  const finePointer = useMediaQuery('(pointer: fine)');
  const tier = useMemo(
    () => (typeof window === 'undefined' ? 'minimal' : detectPerfTier()),
    []
  );
  const budget = PERF_BUDGET[tier];
  const showCursor = finePointer && budget.cursor;

  return (
    <>
      <Analytics />
      {isHome && <Scene3D />}
      {!isHome && (
        <>
          <div
            aria-hidden
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 0,
              pointerEvents: 'none',
              background:
                'radial-gradient(ellipse 55% 40% at 90% 8%, var(--scene-glow-a), transparent 70%)',
            }}
          />
          <RouteShapeAccent />
        </>
      )}
      {showCursor && <Cursor />}

      <div className="relative" style={{ zIndex: 1 }}>
        <Header />
        <Suspense fallback={null}>
          <Outlet />
        </Suspense>
        <Footer />
      </div>
      <StickyWhatsApp />
    </>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to={`/${DEFAULT_LOCALE}`} replace />} />
      <Route path="/:locale" element={<LocaleRoute />}>
        <Route element={<AppChrome />}>
          <Route index element={<HomePage />} />
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="projects/:slug" element={<ProjectPage />} />
          <Route path="blog" element={<BlogPage />} />
          <Route path="blog/:slug" element={<BlogPostPage />} />
          <Route path="services/:slug" element={<ServicePage />} />
          <Route path="villes/:slug" element={<CityPage />} />
          <Route path="devis" element={<QuotePage />} />
          <Route path="terms" element={<LegalPage />} />
          <Route path="privacy" element={<LegalPage />} />
          <Route path="legal/:slug" element={<LegalPage />} />
          <Route path="*" element={<Navigate to={`/${DEFAULT_LOCALE}`} replace />} />
        </Route>
      </Route>
      <Route path="/projects" element={<LegacyLocaleRedirect />} />
      <Route path="/projects/*" element={<LegacyLocaleRedirect />} />
      <Route path="/blog" element={<LegacyLocaleRedirect />} />
      <Route path="/blog/*" element={<LegacyLocaleRedirect />} />
      <Route path="/devis" element={<LegacyLocaleRedirect />} />
      <Route path="/terms" element={<LegacyLocaleRedirect />} />
      <Route path="/privacy" element={<LegacyLocaleRedirect />} />
      <Route path="/legal/*" element={<LegacyLocaleRedirect />} />
      <Route path="/services/*" element={<LegacyLocaleRedirect />} />
      <Route path="/villes/*" element={<LegacyLocaleRedirect />} />
    </Routes>
  );
}
